import events from 'lib/events';
import React from 'react';
import round from 'lodash/round';
import session from 'services/sessionService';
import {chemicalBaseTypes} from './constants';
import {FormattedMessage} from 'react-intl';

// NOTE: this causes a problem for running a test to overwrite existing analyses (mostly on source water page)
// since C# returns null for not set values and Math.min(null, 5) === 0 but Math.min(undefined, 5) === NaN...
// This was causing problems on the iron and copper fields only so using it there since not sure whether
// other parts rely on this behaviour
function minimum(x, y) {
  if (!x && x !== 0 || !y && y === 0) {
    return undefined;
  }
  return Math.min(x, y);
}

function maximum(x, y) {
  if (!x && x !== 0 || !y && y === 0) {
    return undefined;
  }
  return Math.max(x, y);
}


export const canTest = () => {
  if (!session.getUser().privileges.canWaterTest) {
    events.publish('error.user', {message: <FormattedMessage id="water.test.not.permitted" />});
    return false;
  }
  return true;
};

export const scanInstructions = (strip, condition) => {
  if (strip === undefined || !condition) {
    return <div />;
  }

  let prepareInstruction, stripInstruction;

  if (strip === 'Iron') {
    prepareInstruction =
      <p>
        <FormattedMessage id="scan.iron.instructions" />
      </p>
    ;
  } else {
    prepareInstruction =
      <p><FormattedMessage id="scan.prepare.water.sample" /></p>
    ;
  }

  if (strip === 'Halogen' || strip === 'Biguanide') {
    stripInstruction =
      <p>
        <FormattedMessage id="scan.press.green.button" />
      </p>
    ;
  } else if (strip === 'Copper') {
    stripInstruction =
      <p>
        <FormattedMessage id="scan.dip.test.strip" />
      </p>
    ;
  } else if (strip === 'Iron') {
    stripInstruction =
      <p>
        <FormattedMessage id="scan.swish.test.strip" />
      </p>
    ;
  }

  return (
    <div>
      <div className='row'>
        <div className='col-md-4'>
          <label><FormattedMessage id="general.water.sample" /></label>
          { prepareInstruction }
        </div>
        <div className='col-md-4'>
          <label><FormattedMessage id="scan.prepare.test.strip" /></label>
          { stripInstruction }
        </div>
        <div className='col-md-4'>
          <label><FormattedMessage id="scan.run.analysis" /></label>
          <p><FormattedMessage id="scan.allow.test.complete" /></p>
        </div>
      </div>
    </div>
  );
};

export const stripTypes = [
  {
    key: 'Halogen',
    label: 'Halogen V',
  },
  {
    key: 'Biguanide',
    label: 'Biguanide IV',
  },
  {
    key: 'Copper',
    label: 'Copper III',
  },
  {
    key: 'Iron',
    label: 'Iron II',
  },
];

export const discTypes = (chemicalBaseType = 2) => {
  if ([
    chemicalBaseTypes.chlorinePool,
    chemicalBaseTypes.mineralSpringsPool,
    chemicalBaseTypes.brominePool,
    chemicalBaseTypes.chlorinePool,
    chemicalBaseTypes.chlorineSpa,
    chemicalBaseTypes.bromineSpa,
    chemicalBaseTypes.mineralSpringsPool,
    chemicalBaseTypes.saltWaterPool,
    chemicalBaseTypes.saltWaterSpa,
    chemicalBaseTypes.synergyPool,
    chemicalBaseTypes.synergySpa,
  ].indexOf(chemicalBaseType) !== -1) {
    return [
      '26723BIO Series Halogen (CL/BR) Disk II',
      '26722BIO Series Halogen (CL/BR)',
    ];
  }

  if ([
    chemicalBaseTypes.softswimPool,
    chemicalBaseTypes.softSoakSpa,
  ].indexOf(chemicalBaseType) !== -1) {
    return [
      '26724BIO Biguanide Disk',
      '26725BIO Biguanide Disk II',
    ];
  }

  return [
    'Callibration Check Disk',
  ];
};

export const getStripsAvailable = sanitizerName => {
  let stripsAvailable = [];

  if (sanitizerName === 'SoftSwim B' || sanitizerName === 'SoftSoak') {
    stripsAvailable.push(stripTypes[1]);
  } else {
    stripsAvailable.push(stripTypes[0]);
  }
  stripsAvailable = stripsAvailable.concat(stripTypes.slice(2, 4));

  return stripsAvailable;
};

export const fromScannerFormat = {
  pH: 'pH',

  // Accuscan Fields
  BOR: 'optimizerPlusBorate',
  BROM: 'totalBromine',
  CYA: 'cYA',
  DD: 'dD',
  FC: 'freeChlorine',
  HD: 'hardness',
  TA: 'alkalinity',
  TC: 'totalChlorine',
  BIG: ['sSWB', 'sSKSanitizerResidual'],
  PEX: ['sSWC', 'sSKShockResidual'],
  CU: 'copper',
  FE: 'iron',

  // SmartLab Fields
  'Free Cl': 'freeChlorine',
  'Total Cl': 'totalChlorine',
  Alkalinity: 'alkalinity',
  Borate: 'optimizerPlusBorate',
  Copper: 'copper',
  Hardness: 'hardness',
  Iron: 'iron',
  Stabilizer: 'cYA',
  Bromine: 'totalBromine',
  Biguanide: ['sSWB', 'sSKSanitizerResidual'],
  'Biguanide Shock': ['sSWC', 'sSKShockResidual'],

  // Typo in new lamotte library
  Alkalynity: 'alkalinity',
};

export const scanDisabledFields = {
  Accuscan: {
    Halogen: [
      'alkalinity',
      'cYA',
      'dD',
      'freeChlorine',
      'hardness',
      'optimizerPlusBorate',
      'pH',
      'totalBromine',
      'totalChlorine',
    ],
    Biguanide: [
      'alkalinity',
      'cYA',
      'dD',
      'freeChlorine',
      'hardness',
      'optimizerPlusBorate',
      'pH',
      'sSKSanitizerResidual',
      'sSKShockResidual',
      'sSWB',
      'sSWC',
      'totalBromine',
      'totalChlorine',
    ],
    Copper: [
      'copper',
    ],
    Iron: [
      'iron',
    ],
  },
  SmartLab: [
    'alkalinity',
    'copper',
    'cYA',
    'freeChlorine',
    'hardness',
    'iron',
    'optimizerPlusBorate',
    'pH',
    'sSKSanitizerResidual',
    'sSKShockResidual',
    'sSWB',
    'sSWC',
    'stabilizer',
    'totalBromine',
    'totalChlorine',
  ],
};

export const calibration = {
  chlorine: [
    {
      name: 'Free Chlorine',
      field: 'Free Cl',
    },
    {
      name: 'Total Chlorine',
      field: 'Total Cl',
    },
    {
      name: 'Total Alkalinity',
      field: 'Alkalinity',
    },
    {
      name: 'pH',
      field: 'pH',
    },
    {
      name: 'Calcium Hardness',
      field: 'Hardness',
    },
    {
      name: 'Copper',
      field: 'Copper',
    },
    {
      name: 'Iron',
      field: 'Iron',
    },
    {
      name: 'Cyanuric Acid',
      field: 'Stabilizer',
    },
    {
      name: 'Borate',
      field: 'Borate',
    },
  ],

  biguanide: [
    {
      name: 'Biguanide',
      field: 'Biguanide',
    },
    {
      name: 'Total Alkalinity',
      field: 'Alkalinity',
    },
    {
      name: 'pH',
      field: 'pH',
    },
    {
      name: 'Calcium Hardness',
      field: 'Hardness',
    },
    {
      name: 'Biguanide Shock',
      field: 'Biguanide Shock',
    },
    {
      name: 'Copper',
      field: 'Copper',
    },
    {
      name: 'Iron',
      field: 'Iron',
    },
    {
      name: 'Borate',
      field: 'Borate',
    },
  ],
};

export const populateFieldsByChemicalBaseType = {
  1: ['totalBromine', 'cYA', 'dD', 'hardness', 'alkalinity', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  2: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  3: ['dD', 'hardness', 'alkalinity', 'sSWB', 'sSWC', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  5: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  6: ['totalBromine', 'dD', 'hardness', 'alkalinity', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  7: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  8: ['dD', 'hardness', 'alkalinity', 'sSKSanitizerResidual', 'sSKShockResidual', 'copper', 'iron', 'pH'],
  12: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  13: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  14: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH', 'optimizerPlusBorate'],
  15: ['cYA', 'dD', 'freeChlorine', 'hardness', 'alkalinity', 'totalChlorine', 'copper', 'iron', 'pH'],
};

export const preprocessData = (data, analysis, selectedScanner, chemicalBaseType) => {
  for (let key in data) {
    let value = fromScannerFormat[key];

    if (!populateFieldsByChemicalBaseType[chemicalBaseType]) {
      continue;
    }

    if (value instanceof Array && populateFieldsByChemicalBaseType[chemicalBaseType] &&
        (populateFieldsByChemicalBaseType[chemicalBaseType].includes(value[0]) || populateFieldsByChemicalBaseType[chemicalBaseType].includes(value[1]))) {
      analysis[value[0]] = round(Math.max(data[key], 0));
      analysis[value[1]] = round(Math.max(data[key], 0));
    } else if (populateFieldsByChemicalBaseType[chemicalBaseType].includes(value)) {
      analysis[value] = Math.max(data[key], 0);
      // Andie: "pH, Total and Free Chlorine, Total Bromine, Copper and Iron should all be one decimal place.
      // Everything else should be an integer value."
      if (['pH', 'freeChlorine', 'totalChlorine', 'totalBromine', 'copper', 'iron'].indexOf(value) > -1) {
        analysis[value] = round(analysis[value], 1);
      } else {
        analysis[value] = round(analysis[value]);
      }
    }
  }

  if (selectedScanner === 'Accuscan') {
    analysis.scanTypeID = 1;
    // Errors in readings
    if (analysis.hardness > 1200) {
      events.publish('error.user', {message: 'Hardness > 1200ppm - Perform visual tests for better accuracy'});
    }

    if (analysis.sSWB > 70) {
      events.publish('error.user', {message: 'SoftSwim B > 70 - Perform visual tests for better accuracy'});
    }

    if (analysis.sSKSanitizerResidual > 70) {
      events.publish('error.user', {message: 'SSK Sanitizer > 70 - Perform visual tests for better accuracy'});
    }

    // Capping values that come back from the scanner
    analysis.alkalinity = minimum(analysis.alkalinity, 400);
    analysis.copper = minimum(analysis.copper, 5);
    analysis.cYA = minimum(analysis.cYA, 150);
    analysis.freeChlorine = minimum(analysis.freeChlorine, 10);
    analysis.hardness = minimum(analysis.hardness, 400);
    analysis.iron = minimum(analysis.iron, 5);
    analysis.optimizerPlusBorate = minimum(analysis.optimizerPlusBorate, 90);
    analysis.pH = maximum(analysis.pH, 6.2);
    analysis.pH = minimum(analysis.pH, 8.4);
    analysis.dD = minimum(10, maximum(analysis.dD, 1));
    analysis.totalBromine = minimum(analysis.totalBromine, 33);
    analysis.totalChlorine = minimum(analysis.totalChlorine, 10);
    // Biguanide
    analysis.sSWB = minimum(analysis.sSWB, 52);
    analysis.sSKSanitizerResidual = minimum(analysis.sSKSanitizerResidual, 52);
    // Peroxide
    analysis.sSWC = minimum(analysis.sSWC, 50);
    analysis.sSKShockResidual = minimum(analysis.sSKShockResidual, 50);
    // FC = TC when FC > TC.
    analysis.freeChlorine = minimum(analysis.freeChlorine, analysis.totalChlorine);
  }

  if (selectedScanner === 'SmartLab') {
    analysis.scanTypeID = 2;

    // Capping values that come back from the scanner
    analysis.alkalinity = minimum(analysis.alkalinity, 250);
    analysis.copper = minimum(analysis.copper, 3);
    analysis.cYA = minimum(analysis.cYA, 150);
    analysis.freeChlorine = minimum(analysis.freeChlorine, 15);
    analysis.hardness = minimum(analysis.hardness, 1200);
    analysis.iron = minimum(analysis.iron, 3);
    analysis.optimizerPlusBorate = minimum(analysis.optimizerPlusBorate, 60);
    analysis.pH = maximum(analysis.pH, 6.6);
    analysis.pH = minimum(analysis.pH, 8.6);
    analysis.totalChlorine = minimum(analysis.totalChlorine, 15);
    // Biguanide
    analysis.sSWB = minimum(analysis.sSWB, 70);
    analysis.sSKSanitizerResidual = minimum(analysis.sSKSanitizerResidual, 70);
    // Peroxide
    analysis.sSWC = minimum(analysis.sSWC, 60);
    analysis.sSKShockResidual = minimum(analysis.sSKShockResidual, 60);
  }

  return analysis;
};
